import React, { useState, useMemo, useEffect } from 'react'
import { Card, CardMedia, LinearProgress, Typography, Box, Modal, IconButton } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import CloseIcon from '@mui/icons-material/Close'
import { HordeStatus } from '@shared-types'
import { PyrenzBlueButton } from '@client/theme'

interface ImageDisplayProps {
  images: string[]
  isLoading: boolean
  status?: HordeStatus
  onReset?: () => void
}

export function ImageDisplay({ images, isLoading, status, onReset }: ImageDisplayProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const [currentImage, setCurrentImage] = useState<string | null>(null)
  const [animatedProgress, setAnimatedProgress] = useState(0)

  const progress = useMemo(() => {
    if (!status) return 0
    const totalSteps = status.queue_position + status.waiting + status.processing + status.finished
    if (totalSteps === 0) return 0
    return Math.min(
      100,
      Math.round(((status.processing + status.finished) / totalSteps) * 100)
    )
  }, [status])

  const totalRemaining = useMemo(() => {
    if (!status) return 0
    return status.queue_position + status.waiting
  }, [status])

  useEffect(() => {
    if (!isLoading) {
      setAnimatedProgress(0)
      return
    }
    const interval = setInterval(() => {
      setAnimatedProgress(prev => (prev >= progress ? prev : prev + 1))
    }, 50)
    return () => clearInterval(interval)
  }, [progress, isLoading])

  const openModal = (image: string) => {
    setCurrentImage(image)
    setModalOpen(true)
  }

  const closeModal = () => setModalOpen(false)

  const handleDownload = () => {
    if (!currentImage) return
    const link = document.createElement('a')
    link.href = currentImage
    link.download = 'generated_image.png'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {images.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <PyrenzBlueButton
            variant="contained"
            sx={{ bgcolor: 'red', '&:hover': { bgcolor: 'darkred' } }}
            onClick={onReset}
          >
            Reset Images
          </PyrenzBlueButton>
        </Box>
      )}

      {isLoading && (
        <Card sx={{ p: 2, bgcolor: '#1e1e1e', borderRadius: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1, textAlign: 'center' }}>
            {status?.queue_position ? `Queued (#${status.queue_position})` : 'Generating images...'}
          </Typography>

          <LinearProgress
            variant="determinate"
            value={animatedProgress}
            sx={{
              height: 14,
              borderRadius: 7,
              mb: 1,
              backgroundColor: '#333',
              '& .MuiLinearProgress-bar': { bgcolor: '#4caf50' },
            }}
          />

          <Typography variant="caption" sx={{ color: '#ccc', textAlign: 'center' }}>
            Progress: {animatedProgress}% | Waiting: {status?.waiting ?? 0} | Processing: {status?.processing ?? 0}
          </Typography>
        </Card>
      )}

      {images.length === 0 && !isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 300,
            color: '#888',
            fontStyle: 'italic',
          }}
        >
          No images yet. Generate some to see them here!
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {images.map((img, idx) => (
          <Card
            key={idx}
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.03)' },
              width: 200,
              height: 200,
              overflow: 'hidden',
              borderRadius: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onClick={() => openModal(img)}
          >
            <CardMedia
              component="img"
              image={img}
              alt={`Generated-${idx}`}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Card>
        ))}
      </Box>

      <Modal open={modalOpen} onClose={closeModal}>
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: '#121212',
            boxShadow: 24,
            p: 2,
            maxHeight: '90vh',
            overflowY: 'auto',
            width: '80vw',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            color: '#fff',
          }}
        >
          <Box sx={{ alignSelf: 'flex-end' }}>
            <IconButton onClick={closeModal} color="inherit">
              <CloseIcon />
            </IconButton>
          </Box>

          {currentImage && (
            <>
              <img src={currentImage} alt="Generated" style={{ maxWidth: '100%', borderRadius: 8 }} />
              {status && (
                <Box sx={{ mt: 2, width: '100%' }}>
                  <Typography variant="subtitle2">Queue Position: {status.queue_position}</Typography>
                  <Typography variant="subtitle2">Waiting: {status.waiting}</Typography>
                  <Typography variant="subtitle2">Processing: {status.processing}</Typography>
                  <Typography variant="subtitle2">Wait Time: {status.wait_time}s</Typography>
                  <Typography variant="subtitle2">Done: {status.done ? 'Yes' : 'No'}</Typography>
                  <Typography variant="subtitle2">Faulted: {status.faulted ? 'Yes' : 'No'}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <IconButton onClick={handleDownload} color="inherit">
                  <DownloadIcon />
                </IconButton>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  )
}
